import React from 'react';

// Presentational panels from the design (§03): category-grouped catalog + "Vinculado a" hub.
const mono = "'JetBrains Mono',monospace";
const eyebrow: React.CSSProperties = { font: `600 10px ${mono}`, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-faint,#98a39d)' };

export interface LinkVM { label: string; value: string; ok: boolean; action: string; onAction?: () => void }
export interface ProcedureCardVM { id: number; name: string; dur: string; priceFmt: string; tone: string; toneDark: string; linkOk: boolean[]; }
export interface ProcedureGroupVM { cat: string; items: ProcedureCardVM[] }
export interface ProcedureDetailVM { cat: string; name: string; dur: string; priceFmt: string; monthUse: string; links: LinkVM[] }

const WARN_CSS = 'body.dark .pp-warn{color:#d6a85f !important}';

export const ProcedureCatalog: React.FC<{
  groups: ProcedureGroupVM[]; selectedId?: number | null; onSelect: (id: number) => void;
  health: (configured: number, total: number) => string;
}> = ({ groups, selectedId, onSelect, health }) => (
  <div style={{ flex: '1 1 620px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
    <style>{WARN_CSS}</style>
    {groups.map(g => (
      <div key={g.cat} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ font: '500 17px Fraunces,Georgia,serif' }}>{g.cat}</span>
          <span style={{ font: `400 11px ${mono}`, color: 'var(--ink-faint,#98a39d)' }}>{g.items.length}</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 10 }}>
          {g.items.map(pr => {
            const on = pr.id === selectedId;
            const ok = pr.linkOk.filter(Boolean).length;
            const missing = pr.linkOk.length - ok;
            return (
              <div key={pr.id} onClick={() => onSelect(pr.id)} style={{ background: 'var(--surface,#fff)', border: `1px solid ${on ? 'var(--accent,#0b3b2e)' : 'var(--line,#e5ded1)'}`, boxShadow: on ? '0 0 0 3px rgba(11,59,46,.10)' : 'none', borderRadius: 10, padding: 14, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: 500 }}>{pr.name}</div>
                  <span style={{ width: 14, height: 14, borderRadius: 4, flex: 'none', background: pr.tone, boxShadow: `inset 3px 0 0 ${pr.toneDark}` }} />
                </div>
                <div style={{ display: 'flex', gap: 12, font: `400 12px ${mono}`, color: 'var(--ink-muted,#5a6660)' }}><span>{pr.dur}</span><span>{pr.priceFmt}</span></div>
                <div className={missing >= 3 ? 'pp-warn' : undefined} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: missing >= 3 ? '#7a5a26' : 'var(--ink-muted,#5a6660)' }}>
                  <span style={{ display: 'flex', gap: 3 }}>{pr.linkOk.map((o, i) => <span key={i} style={{ width: 6, height: 6, borderRadius: 3, background: o ? '#3d6e4f' : 'var(--line,#e5ded1)' }} />)}</span>
                  {health(ok, pr.linkOk.length)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ))}
  </div>
);

export const ProcedureHubPanel: React.FC<{
  p: ProcedureDetailVM; linkedTo: string; archiveHint: React.ReactNode; archiveLabel: string; onArchive?: () => void;
}> = ({ p, linkedTo, archiveHint, archiveLabel, onArchive }) => (
  <div style={{ flex: '1 1 340px', maxWidth: 420, minWidth: 0, background: 'var(--surface,#fff)', border: '1px solid var(--line,#e5ded1)', borderRadius: 14, overflow: 'hidden' }}>
    <div style={{ padding: 20, borderBottom: '1px solid var(--line-soft,#efeae0)', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={eyebrow}>{p.cat}</div>
      <div style={{ font: '500 22px/1.15 Fraunces,Georgia,serif' }}>{p.name}</div>
      <div style={{ display: 'flex', gap: 14, font: `400 12px ${mono}`, color: 'var(--ink-muted,#5a6660)' }}><span>{p.dur}</span><span>{p.priceFmt}</span><span>{p.monthUse}</span></div>
    </div>
    <style>{WARN_CSS}</style>
    <div style={{ ...eyebrow, padding: '16px 20px 4px' }}>{linkedTo}</div>
    <div style={{ display: 'flex', flexDirection: 'column', padding: '0 8px 8px' }}>
      {p.links.map(l => (
        <div key={l.label} style={{ display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: 10, alignItems: 'center', padding: '10px 12px', borderRadius: 6 }}>
          <span style={{ width: 18, height: 18, borderRadius: '50%', display: 'grid', placeItems: 'center', font: '700 10px Inter,sans-serif', background: l.ok ? '#e7efe9' : '#f4ead9', color: l.ok ? '#3d6e4f' : '#7a5a26' }}>{l.ok ? '✓' : '!'}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{l.label}</div>
            <div className={l.ok ? undefined : 'pp-warn'} style={{ fontSize: 12, color: l.ok ? 'var(--ink-muted,#5a6660)' : '#7a5a26' }}>{l.value}</div>
          </div>
          <button onClick={l.onAction} style={{ height: 28, padding: '0 10px', borderRadius: 6, border: '1px solid var(--line,#e5ded1)', background: 'var(--surface,#fff)', color: l.ok ? 'var(--ink-muted,#5a6660)' : 'var(--accent,#0b3b2e)', font: '500 12px Inter,sans-serif', cursor: 'pointer', whiteSpace: 'nowrap', minHeight: 0 }}>{l.action}</button>
        </div>
      ))}
    </div>
    <div style={{ padding: '12px 20px 18px', borderTop: '1px solid var(--line-soft,#efeae0)', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: 'var(--ink-muted,#5a6660)' }}>{archiveHint}</span>
      <button onClick={onArchive} style={{ height: 30, padding: '0 12px', borderRadius: 6, border: '1px solid var(--line,#e5ded1)', background: 'var(--surface,#fff)', color: 'var(--ink-muted,#5a6660)', font: '500 12px Inter,sans-serif', cursor: 'pointer', flex: 'none', minHeight: 0 }}>{archiveLabel}</button>
    </div>
  </div>
);
